import { AddOnItemName } from "./dashboard";

export enum Plans {
  FREE = "free",
  STARTER = "starter",
  PRO = "pro",
  VA = "va",
  PRO_YEARLY = "pro_yearly",
  STARTER_YEARLY = "starter_yearly",
  VA_YEARLY = "va_yearly",
  AUTOPILOT = "autopilot",
  AUTOPILOT_YEARLY = "autopilot_yearly",
  PRO_QUARTERLY = "pro_quarterly",
  VA_QUARTERLY = "va_quarterly",
  AUTOPILOT_QUARTERLY = "autopilot_quarterly",
}

export const QUARTERLY_PLANS = [
  Plans.PRO_QUARTERLY,
  Plans.VA_QUARTERLY,
  Plans.AUTOPILOT_QUARTERLY,
];

export const isQuarterlyPlan = (plan?: Plans | string | null): boolean =>
  QUARTERLY_PLANS.includes(plan as Plans);

// The regular monthly plan each quarterly (B2GO) plan reverts to — used to show
// the struck-through regular monthly rate against the B2GO effective per-month price.
export const QUARTERLY_BASE_MONTHLY_PLAN: { [key in Plans]?: Plans } = {
  [Plans.PRO_QUARTERLY]: Plans.PRO,
  [Plans.VA_QUARTERLY]: Plans.VA,
  [Plans.AUTOPILOT_QUARTERLY]: Plans.AUTOPILOT,
};

export enum OverridePlanName {
  PRO = "pro",
  STARTER = "starter",
  VA = "va",
  AUTOPILOT = "autopilot",
}

// The plan "family" each plan belongs to, independent of billing cycle (monthly,
// yearly, or quarterly/B2GO). Used so add-on availability is the same regardless of
// how the plan is billed.
export const PLAN_FAMILY: { [key in Plans]?: OverridePlanName } = {
  [Plans.PRO]: OverridePlanName.PRO,
  [Plans.PRO_YEARLY]: OverridePlanName.PRO,
  [Plans.PRO_QUARTERLY]: OverridePlanName.PRO,
  [Plans.VA]: OverridePlanName.VA,
  [Plans.VA_YEARLY]: OverridePlanName.VA,
  [Plans.VA_QUARTERLY]: OverridePlanName.VA,
  [Plans.AUTOPILOT]: OverridePlanName.AUTOPILOT,
  [Plans.AUTOPILOT_YEARLY]: OverridePlanName.AUTOPILOT,
  [Plans.AUTOPILOT_QUARTERLY]: OverridePlanName.AUTOPILOT,
  [Plans.STARTER]: OverridePlanName.STARTER,
  [Plans.STARTER_YEARLY]: OverridePlanName.STARTER,
};

export enum BillingCycle {
  monthly = "monthly",
  yearly = "yearly",
  quarterly = "quarterly",
}

const FAMILY_CYCLE_PLAN: {
  [key in OverridePlanName]?: { [cycle in BillingCycle]?: Plans };
} = {
  [OverridePlanName.PRO]: {
    [BillingCycle.monthly]: Plans.PRO,
    [BillingCycle.yearly]: Plans.PRO_YEARLY,
    [BillingCycle.quarterly]: Plans.PRO_QUARTERLY,
  },
  [OverridePlanName.VA]: {
    [BillingCycle.monthly]: Plans.VA,
    [BillingCycle.yearly]: Plans.VA_YEARLY,
    [BillingCycle.quarterly]: Plans.VA_QUARTERLY,
  },
  [OverridePlanName.AUTOPILOT]: {
    [BillingCycle.monthly]: Plans.AUTOPILOT,
    [BillingCycle.yearly]: Plans.AUTOPILOT_YEARLY,
    [BillingCycle.quarterly]: Plans.AUTOPILOT_QUARTERLY,
  },
  [OverridePlanName.STARTER]: {
    [BillingCycle.monthly]: Plans.STARTER,
    [BillingCycle.yearly]: Plans.STARTER_YEARLY,
  },
};

// The plan variant for a given family + billing cycle, e.g. (pro, "quarterly") → pro_quarterly.
export const getPlanForCycle = (
  plan: Plans,
  cycle: BillingCycle,
): Plans | undefined => {
  const family = PLAN_FAMILY[plan];
  if (!family) return undefined;
  return FAMILY_CYCLE_PLAN[family]?.[cycle];
};

export const getCycleForPlan = (plan?: Plans | null): BillingCycle => {
  if (plan && QUARTERLY_PLANS.includes(plan)) return BillingCycle.quarterly;
  if (
    plan &&
    [
      Plans.PRO_YEARLY,
      Plans.VA_YEARLY,
      Plans.AUTOPILOT_YEARLY,
      Plans.STARTER_YEARLY,
    ].includes(plan)
  ) {
    return BillingCycle.yearly;
  }
  return BillingCycle.monthly;
};

export const getEquivalentPlansFromOverride = (
  overridePlanName: OverridePlanName,
) => {
  const plans: { [key: string]: Plans[] } = {
    [OverridePlanName.PRO]: [Plans.PRO, Plans.PRO_YEARLY, Plans.PRO_QUARTERLY],
    [OverridePlanName.STARTER]: [Plans.STARTER, Plans.STARTER_YEARLY],
    [OverridePlanName.VA]: [Plans.VA, Plans.VA_YEARLY, Plans.VA_QUARTERLY],
    [OverridePlanName.AUTOPILOT]: [
      Plans.AUTOPILOT,
      Plans.AUTOPILOT_YEARLY,
      Plans.AUTOPILOT_QUARTERLY,
    ],
  };
  return overridePlanName in plans ? plans[overridePlanName] : [];
};

export const DisplayName: { [key: string]: string } = {
  [Plans.FREE]: "Free",
  [Plans.STARTER]: "Starter",
  [Plans.PRO]: "Pro",
  [Plans.VA]: "AI Virtual Assistant",
  [Plans.PRO_YEARLY]: "Pro",
  [Plans.STARTER_YEARLY]: "Starter",
  [Plans.VA_YEARLY]: "AI Virtual Assistant",
  [Plans.AUTOPILOT]: "Autopilot",
  [Plans.AUTOPILOT_YEARLY]: "Autopilot",
  [Plans.PRO_QUARTERLY]: "Pro",
  [Plans.VA_QUARTERLY]: "AI Virtual Assistant",
  [Plans.AUTOPILOT_QUARTERLY]: "Autopilot",
  [AddOnItemName.partnership_data]: "Partnership Data",
  [AddOnItemName.custom_email]: "Your Professional Email Setup",
  [AddOnItemName.support_call]: "1:1 Onboarding Call",
};
